import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 9000-9999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const sharedErrors = {
  // The generic "this file does not exist" refusal, sent code-only from escrow.mongo.service.ts,
  // fileSystemServer/{filesystem-server,fileSystemServer}.service.ts and
  // selectionSet/selectionSetMongo.service.ts.
  //
  // NOT the code fileOperations' createFileVersion sends: that uses FILE_NOT_AVAILABLE (29025,
  // "This file is not available!"). The two are easy to confuse — check which one a consumer
  // actually compares against before reusing either.
  FILE_NOTFOUND: {
    errorCode: 9100,
    errorMessage: t("File not found"),
    isDynamic: false,
  },
  // A "new version" upload named an original file that does not exist — a distinct upload
  // precondition, not a plain lookup miss like FILE_NOTFOUND. Split out of FILE_NOTFOUND (2026-09-10).
  // Split out of FILE_NOTFOUND this session — escrow.mongo.service.ts's "upload as new version"
  // precondition (the named original file doesn't exist), a distinct concept from a plain lookup miss.
  // ⚠️ Renumbered 9107 -> 9108. Staging independently shipped 9107 as FILE_DELETE_BLOCKED_BY_REVIEW
  // (below), and staging is what is live; this code has only ever existed on this branch, so it is
  // the one that moves. Same clash the per-block split exists to surface (cf. 7038 in account.ts).
  FILE_ORIGINAL_NOT_FOUND_FOR_VERSION: {
    errorCode: 9108,
    errorMessage: t("Cannot upload as new version as original file does not exist."),
    isDynamic: false,
  },
  // Live at fileOperations/fileOperations.utility.ts:503 (copyFileInSession, feeding
  // consumeExternalFiles/copyFiles). Confirmed safe — FileManagerAsPopup.tsx shows a fixed
  // escrow-specific toast whenever errorCode is present, never reads errorMessage in that branch.
  FILE_ESCROWED: {
    errorCode: 9101,
    errorMessage: t("File is escrowed please check escrow section"),
    isDynamic: false,
  },
  // Only appears in a COMMENTED-OUT line (accounts/account.mongo.service.ts:330) — recaptcha
  // checking looks disabled server-side today. Frontend still has dead magic-number checks for this
  // code (SignInTemplate.tsx, ForgotPassword.tsx) in case it's ever re-enabled — kept at its existing
  // number for that reason (see errorCodes.ts's QUERIES_DISTINCTPROPERTY_NOTFOUND comment for the
  // 9001 collision this avoided).
  RECAPTCHA_UNUSUAL_TRAFFIC: {
    errorCode: 9001,
    errorMessage: t("Unusual traffic detected. Please try again."),
    isDynamic: false,
  },
  // The three CSRF refusals (middleware/csrf.ts). Each sentence repeats its OWN number because that
  // is byte-for-byte what the user sees today: the server sends
  // `The page is out of date, please refresh. Error Code: ${errorCode}` and
  // web/src/services/axiosShared.ts renders that string verbatim. Keeping the numbers in the text
  // also keeps the three entries distinct, so text->code stays unambiguous.
  //
  // The number is INTERPOLATED from the constant rather than typed out, so renumbering the code
  // carries the sentence with it and the two can never drift. Safe as a template literal: the value
  // is a plain `string` at runtime, and nothing reads these sentences out of this file's SOURCE —
  // `translateKeys.js` works from web/public/locales/en.json, not from here.
  //
  // ⚠️ These are NOT yet reached through the mapper: axiosShared reads the body's `errorMessage`
  // directly and never calls resolveErrorContent. They are declared so the registry is complete and
  // so the client can be switched over without a display change.
  CSRF_TOKEN_MISMATCH: {
    errorCode: 9007,
    errorMessage: t("The page is out of date, please refresh. Error Code: 9007"),
    isDynamic: false,
  },
  CSRF_TOKEN_INVALID: {
    errorCode: 9008,
    errorMessage: t("The page is out of date, please refresh. Error Code: 9008"),
    isDynamic: false,
  },
  CSRF_TOKEN_MISSING: {
    errorCode: 9009,
    errorMessage: t("The page is out of date, please refresh. Error Code: 9009"),
    isDynamic: false,
  },
  // Live at 3 sites (fileUpload.controller.ts x2, fileUpload.service.ts x1) via
  // DocumentNumberAmbiguousError — but NOT yet migrated: those sites bridge the Error subclass's
  // native `.message` onto the wire `errorMessage` in code shared with RevisionValidationError
  // (REVISION_INVALID), whose per-violation-reason split is still an open architecture question.
  // This entry exists for reference/future use only — do not assume the wire response is code-only
  // for this one yet.
  DOCUMENT_NUMBER_AMBIGUOUS: {
    errorCode: 9103,
    errorMessage: t(
      "Multiple files in this folder already share this Document Number and file type. Resolve the duplicates before importing.",
    ),
    isDynamic: false,
  },
  // Multer/busboy aborted the request before any handler ran, because a part
  // exceeded a declared upload limit. Caller input, not a server fault, so
  // these are answered with rather than reported.
  // Live at helpers/multipartRoute.ts's MULTER_ERROR_CODES (LIMIT_FILE_SIZE, LIMIT_FILE_COUNT) — the
  // one multipart-upload route (forms' /updateTaskInspectionAndProgress). Confirmed safe: the sole
  // frontend consumer (useForms.tsx) has a generic `errorMessage || SOMETHING_WENT_WRONG` fallback
  // everywhere and no code-specific branch for this code.
  FILE_TOO_LARGE: {
    errorCode: 9104,
    errorMessage: t("The file is too large."),
    isDynamic: false,
  },
  // Live at the same MULTER_ERROR_CODES table (LIMIT_FIELD_VALUE, LIMIT_FIELD_COUNT, LIMIT_FIELD_KEY,
  // LIMIT_PART_COUNT) — same route, same confirmed-safe frontend fallback as FILE_TOO_LARGE above.
  UPLOAD_FIELD_TOO_LARGE: {
    errorCode: 9105,
    errorMessage: t("The upload request is too large."),
    isDynamic: false,
  },
  // The upload is not a type the portal's ALLOWED_FILE_TYPES permits, or not
  // one the target question's own `allowedFileTypes` permits.
  // Live at forms.service.ts:6719,6734 (via FormFileRequestError) — confirmed safe, the sole
  // frontend consumer (useForms.tsx) has a generic `errorMessage || SOMETHING_WENT_WRONG` fallback
  // and no code-specific branch for this code.
  FILE_TYPE_NOT_ALLOWED: {
    errorCode: 9106,
    errorMessage: t("File type not allowed"),
    isDynamic: false,
  },
  // Live at estimates.service.ts:4160-4162. Confirmed safe — EstimateCostDatabaseConfigration.tsx
  // branches on this exact code (raw 9200) to open a confirm-upgrade modal; it never reads
  // errorMessage for this code.
  COST_DATABASE_ITEM_DELETED_IN_LATEST_VERSION: {
    errorCode: 9200,
    errorMessage: t("This Cost Database item has been deleted in the latest version"),
    isDynamic: false,
  },
  // Live at estimates.service.ts:4176-4180,4226-4230. Confirmed safe — reaches the same frontend
  // flow as COST_DATABASE_ITEM_DELETED_IN_LATEST_VERSION above; falls into its fixed-text `else`
  // branch ("The Cost Database version could not be upgraded"), never reads errorMessage.
  COST_DATABASE_ITEM_RATE_DELETED_IN_LATEST_VERSION: {
    errorCode: 9201,
    errorMessage: t("The rate for this Cost Database item has been deleted in the latest version"),
    isDynamic: false,
  },
  // The generic fallback codes need entries of their own: a refusal that legitimately stays generic
  // (an internal guard, a malformed request, a write the database did not acknowledge) still has to
  // render something, and `getEntryByErrorCode` returns undefined for a code with no entry — which is
  // a blank toast, not a fallback. The text is deliberately the same filler these call sites already
  // send inline, so the client renders identically whether or not the site carries an errorMessage.
  SOMETHING_WENT_WRONG: {
    errorCode: 9000,
    errorMessage: t("Something went wrong"),
    isDynamic: false,
  },
  /**
   * Generic companions to SOMETHING_WENT_WRONG, so that every `errorMessage` can be paired with a
   * machine-readable code even where no domain-specific one fits. Prefer a specific code
   * (SPACE_NOT_FOUND, ESTIMATE_NOT_FOUND, INSUFFICIENT_PERMISSION, …) whenever one exists — these
   * exist so a refusal is never left with prose alone.
   */
  RESOURCE_NOT_FOUND: {
    errorCode: 9003,
    errorMessage: t("The requested item could not be found."),
    isDynamic: false,
  },
  /** The write was accepted but changed nothing (no matching row, or the values already matched). */
  // Split complete. 9005 now has ONE call site — the one whose sentence is its canonical message.
  // The rest either carry no message, forward a child's, or interpolate a runtime value. The split
  // produced 94 codes because almost every site named exactly what did not change; "nothing was
  // updated" is rarely the same fact twice.
  NO_CHANGES_APPLIED: {
    errorCode: 9005,
    errorMessage: t("Your changes could not be saved."),
    isDynamic: false,
  },
  /**
   * No valid session/API key on the request — AUTHENTICATION, not authorization. Distinct from
   * ACCOUNT_UNAUTHORIZED_ACTION (7012), which means "signed in, but not allowed to do this".
   * Sent with the 401 that middleware/authorize.ts raises.
   */
  SESSION_INVALID: {
    errorCode: 9006,
    errorMessage: t("Your session has expired. Please sign in again."),
    isDynamic: false,
  },
  /** A required field was absent, malformed, or failed a server-side rule. */
  // Split complete. Every remaining site either carries no message, forwards a validator's own
  // text, or interpolates a runtime value. The split produced 128 codes because almost every site
  // named the exact field or rule that failed.
  // Entries for the last codes that had none. A code with no entry resolves to a BLANK message
  // once the inline sentences are gone, so every code in the registry now has one.
  VALIDATION_FAILED: {
    errorCode: 9004,
    errorMessage: t("Please check the information you entered and try again."),
    isDynamic: false,
  },
  // Client-side transient network failure (axios ERR_NETWORK): request never
  // reached the server or no response came back. Not a server-issued code —
  // synthesized by the axios wrapper so callers see a `{ status: false }` envelope.
  NETWORK_ERROR: {
    errorCode: 9002,
    errorMessage: t("Network error. Please check your connection and try again."),
    isDynamic: false,
  },
  // REVISION_INVALID is the former umbrella code for all eight revision rules. Nothing sends or reads
  // it any more: RevisionValidationError throws the specific per-violation code (25017-25024), and
  // the three callers that used to compare against this one now use `isRevisionRuleErrorCode`.
  // Kept only so an older client that still recognises 9102 has a sentence for it.
  REVISION_INVALID: {
    errorCode: 9102,
    errorMessage: t("The revision does not satisfy this folder's revision rules."),
    isDynamic: false,
  },
  // 14114-14129 split the billing generics (INVOICE_NOT_FOUND / SUBSCRIPTION_NOT_FOUND /
  // PLAN_NOT_FOUND / PAYMENT_NOT_FOUND / *_NOT_ACTIVE) where the site's own sentence named a
  // distinct business state the generic code's sentence does not express — e.g. "Cannot void a paid
  // invoice" is not "Invoice is already paid", and "Can only add addons to active subscriptions" is
  // not "Subscription is not active". Each carries the site's original wording verbatim.
  // 18115-18125 split the contracts generics (SOMETHING_WENT_WRONG / VALIDATION_FAILED /
  // NO_CHANGES_APPLIED) where the site's sentence named a distinct business state. Two of them —
  // CONTRACT_TASK_ACTUAL_START_AFTER_END and CONTRACT_TASK_ACTUAL_END_BEFORE_START — exist because
  // CONTRACT_TASK_DATE_INVALID was carrying TWO different sentences at two different call sites.
  // 7130-7134 split accounts sites off SOMETHING_WENT_WRONG where the sentence named a real,
  // user-triggerable state. The `register1` shim handlers' `safeParse` guards are deliberately NOT
  // here: a body that fails the request schema is a malformed request, not a business state, so
  // those keep VALIDATION_FAILED and their own inline prose.
  // Tail of the category-B split: one code per distinct sentence, each in its own module's range.
  // 7130-7134: each carries VERBATIM the sentence its accounts call site used to send inline.
  // Tail of the category-B split — each carries its call site's sentence verbatim.
  COST_DATABASE_ITEM_NOT_IN_LATEST_VERSION: {
    errorCode: 9202,
    errorMessage: t("No active Cost Database item matches that itemId in the database's latest version."),
    isDynamic: false,
  },
  COST_DATABASE_VARIABLE_RATE_RULESET_INVALID: {
    errorCode: 9203,
    errorMessage: t(
      "The variable-rate ruleset is not valid — bands must start at 0, run continuously without gaps or overlaps, and carry positive numeric rangeTo, differentialRate and perUnitRateLeastCount values.",
    ),
    isDynamic: false,
  },
  SERVER_UNAVAILABLE: {
    errorCode: 9010,
    errorMessage: t(
      "A firewall or proxy issue interrupted the request. Please try again, or contact your system administrator.",
    ),
    isDynamic: false,
  },
  SESSION_API_KEY_INVALID_FOR_PORTAL: {
    errorCode: 9011,
    errorMessage: t("The API key is not valid for this portal."),
    isDynamic: false,
  },
  // A caught exception's OWN message, passed through to the user unchanged. Its mapper template is a
  // bare `{{message}}`, so the rendered sentence is exactly what `error.message` held — which is what
  // staging sends at these sites (`errorMessage: error instanceof Error ? error.message : "Something
  // went wrong"`). Collapsing those to SOMETHING_WENT_WRONG changed what the user was told, so the
  // text travels in `params.message` instead, with the same "Something went wrong" fallback for a
  // non-Error throw.
  // Bare `{{message}}` on purpose: the whole sentence IS the caught exception's message, so what the
  // user sees is byte-identical to the `errorMessage` staging sends at these catch sites.
  UNEXPECTED_ERROR_WITH_MESSAGE: {
    errorCode: 9012,
    errorMessage: t("{{message}}"),
    isDynamic: true,
    dynamicParams: ["message"],
  },
  // A list route's `nextPageToken` it cannot continue from (packages/server/helpers/pageToken.ts): not a
  // token it issued, or the row the token points after no longer exists. Reloading starts a new first page.
  INVALID_PAGE_TOKEN: {
    errorCode: 9013,
    errorMessage: t("This list changed while it was loading. Reload it to see the rest."),
    isDynamic: false,
  },
  // ==========================================================================================
  // 9300-9308 — CUBE AI (CP-1361), arrived with staging.
  //
  // ⚠️ Staging sent ONE code, AI_ACCESS_DENIED (9301), for five different sentences, discriminated
  // by a separate `reason` string. Under one-code-one-sentence that would show four of the five the
  // wrong text, and nothing on the client reads `reason` — the two AiUsage.tsx screens printed the
  // server's prose verbatim. So each refusal gets its own code, and 9301 keeps the subscription arm.
  // ==========================================================================================
  AI_SEATS_UNAVAILABLE: {
    errorCode: 9300,
    errorMessage: t("CUBE AI seats are not available. Every purchased seat is already assigned."),
    isDynamic: false,
  },
  AI_SUBSCRIPTION_NOT_LIVE: {
    errorCode: 9301,
    errorMessage: t("CUBE AI is unavailable because this portal's subscription is not active."),
    isDynamic: false,
  },
  // The two limit refusals interpolate a humanised duration built by `formatResetDuration`
  // (aiSeats.service.ts). It is a rendered string, not a number, so it travels as `params.duration`.
  AI_CYCLE_LIMIT: {
    errorCode: 9302,
    errorMessage: t("You've reached your CUBE AI limit. Resets in {{duration}}."),
    isDynamic: true,
    dynamicParams: ["duration"],
  },
  AI_SESSION_LIMIT: {
    errorCode: 9303,
    errorMessage: t("You've hit the 5-hour CUBE AI session limit. Resets in {{duration}}."),
    isDynamic: true,
    dynamicParams: ["duration"],
  },
  AI_NOT_A_MEMBER: {
    errorCode: 9304,
    errorMessage: t("You are not an active member of this portal."),
    isDynamic: false,
  },
  AI_NO_SEAT: {
    errorCode: 9305,
    errorMessage: t("You don't have a CUBE AI seat. Ask a portal admin to assign one in Portal Users."),
    isDynamic: false,
  },
  AI_SEAT_OVER_QUOTA: {
    errorCode: 9306,
    errorMessage: t(
      "Your CUBE AI seat is above the number of seats this portal has purchased. Ask a portal admin to free a seat or buy more.",
    ),
    isDynamic: false,
  },
  AI_NO_PRODUCT_SEAT: {
    errorCode: 9307,
    errorMessage: t("CUBE AI chat needs a product seat (CDE, PM or DT) as well as a CUBE AI seat. Ask a portal admin."),
    isDynamic: false,
  },
  // Distinct from AI_NOT_A_MEMBER (9304): that one addresses the CALLER, this one is an admin being
  // told about the user they are assigning a seat to. Staging paired it with ACCOUNT_NOT_FOUND
  // (7001, "Account not found"), which says something different.
  AI_TARGET_NOT_A_PORTAL_MEMBER: {
    errorCode: 9308,
    errorMessage: t("This user is not an active member of the portal."),
    isDynamic: false,
  },
  // Staging's whole-batch refusal when every selected item is attached to a Review. The sentence is
  // the server's own (fileSystemServer.service.ts's FILES_IN_REVIEWS_DELETE_FAILED_MESSAGE) rather
  // than the shorter one staging's FileDelete hook hardcoded, because it is a superset: it keeps the
  // actionable "Remove them from those Reviews first" that API and MCP clients already received.
  FILE_DELETE_BLOCKED_BY_REVIEW: {
    errorCode: 9107,
    errorMessage: t("Some item(s) could not be deleted as they are added to Reviews. Remove them from those Reviews first."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
